import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { HeaderPro } from '../../components/HeaderPro';
import { Footer } from '../../components/Footer';
import { Pagination } from '../../components/Pagination';
import {
  createProConnectDashboardLink,
  createProConnectOnboardingLink,
  createProConnectUpdateLink,
  getProConnectStatus,
  getProTaxUtrStatus,
  listProBookings,
  listProPayouts,
  type ProBookingRow,
  type ProConnectStatus,
  type ProPayoutRow,
} from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { downloadProPayoutInvoice } from '../../../lib/pro-payout-invoice';

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

// Keyed by RAW status (fallback when the API build is older and doesn't send
// display_* yet). Note PAID no longer says "Paid" — the money is only in the
// pro's Stripe balance at that point, not their bank.
const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pending',
  PAID: 'Processing',
  FAILED: 'Failed',
  REVERSED: 'Reversed',
  SKIPPED: 'Awaiting setup',
};

// Keyed by display_status (preferred) OR raw status (fallback). Both key sets
// are covered so whichever the row carries resolves to a colour.
const STATUS_STYLE: Record<string, string> = {
  // display_status values
  PROCESSING: 'bg-sky-50 text-sky-700 ring-1 ring-sky-100',
  IN_TRANSIT: 'bg-blue-50 text-blue-700 ring-1 ring-blue-100',
  DEPOSITED: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
  ON_HOLD: 'bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200',
  // raw status fallbacks
  PENDING: 'bg-amber-50 text-amber-700 ring-1 ring-amber-100',
  PAID: 'bg-sky-50 text-sky-700 ring-1 ring-sky-100',
  FAILED: 'bg-red-50 text-red-700 ring-1 ring-red-100',
  REVERSED: 'bg-purple-50 text-purple-700 ring-1 ring-purple-100',
  SKIPPED: 'bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200',
};

const PAGE_SIZE = 10;

export default function PayoutsPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState<ProConnectStatus | null>(null);
  // null = not yet loaded; drives the "UTR on file / Add" status line.
  const [hasTaxUtr, setHasTaxUtr] = useState<boolean | null>(null);
  const [payouts, setPayouts] = useState<ProPayoutRow[]>([]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [cancellations, setCancellations] = useState<ProBookingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  // Each table paginates independently — pro with many payouts shouldn't
  // also lose the cancellations list off the bottom of the page.
  const [payoutsPage, setPayoutsPage] = useState(1);
  const [cancellationsPage, setCancellationsPage] = useState(1);

  async function load() {
    setLoading(true);
    try {
      // Status call refreshes Connect state from Stripe — so if the
      // pro just came back from Stripe-hosted onboarding via
      // ?stripe=return, the local cache is up to date immediately.
      // Pull pro-initiated cancellations in parallel — every one
      // refunded the customer, so it's money the pro didn't earn.
      const [statusRes, payoutsRes, cancelledRes, taxRes] = await Promise.all([
        getProConnectStatus(),
        listProPayouts({ limit: 50 }),
        listProBookings({ limit: 50, status: 'CANCELLED_BY_PRO' }),
        // Non-fatal — a UTR-status hiccup shouldn't blank the payouts page.
        getProTaxUtrStatus().catch(() => ({ has_tax_utr: false }) as any),
      ]);
      setStatus(statusRes.info);
      setPayouts(payoutsRes.data);
      setTotals(payoutsRes.totals_by_status ?? {});
      setCancellations(cancelledRes.data ?? []);
      setHasTaxUtr(!!taxRes.has_tax_utr);
    } catch (err: any) {
      toast.error(
        apiErrorMessage(err, 'Failed to load'),
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  // Stripe redirects back with ?stripe=return or ?stripe=refresh on
  // its hosted onboarding flow. Either way we reload the page state
  // so the customer sees the freshest Connect status.
  useEffect(() => {
    if (params.get('stripe')) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  async function handleStartOnboarding() {
    setRedirecting(true);
    try {
      const res = await createProConnectOnboardingLink();
      window.location.href = res.url;
    } catch (err: any) {
      toast.error(
        apiErrorMessage(err, 'Could not start'),
      );
      setRedirecting(false);
    }
  }

  // Stripe-hosted Express dashboard. Single-use, ~5 min validity, so
  // we open in the same tab — re-clicking gets a fresh link.
  async function handleOpenStripeDashboard() {
    setRedirecting(true);
    try {
      const res = await createProConnectDashboardLink();
      window.location.href = res.url;
    } catch (err: any) {
      toast.error(
        apiErrorMessage(err, 'Could not open dashboard'),
      );
      setRedirecting(false);
    }
  }

  const payoutsTotalPages = Math.max(
    1,
    Math.ceil(payouts.length / PAGE_SIZE),
  );
  const cancellationsTotalPages = Math.max(
    1,
    Math.ceil(cancellations.length / PAGE_SIZE),
  );
  const pagedPayouts = payouts.slice(
    (payoutsPage - 1) * PAGE_SIZE,
    payoutsPage * PAGE_SIZE,
  );
  const pagedCancellations = cancellations.slice(
    (cancellationsPage - 1) * PAGE_SIZE,
    cancellationsPage * PAGE_SIZE,
  );

  const canReceivePayouts = status?.stripe_account_status === 'ACTIVE';
  const needsAction =
    !status ||
    status.stripe_account_status === 'NOT_STARTED' ||
    status.stripe_account_status === 'PENDING' ||
    status.stripe_account_status === 'RESTRICTED';

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <HeaderPro />

      <main className="flex-1 pb-16">
        <div className="max-w-[1140px] mx-auto px-4 py-8">
          {/* Back button matches the chevron-square pattern used on
              the other settings pages so the pro can return to
              Account settings (the typical entry point) with one
              click. */}
          <div className="flex items-center gap-4 mb-6">
            <Link
              to="/professional/account-settings"
              className="mt-1 w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors shrink-0"
              aria-label="Back to account settings"
              title="Back to account settings"
            >
              <span className="material-icons text-[#908e8e] text-[18px]">
                chevron_left
              </span>
            </Link>
            <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] leading-[32px]">
              Payouts
            </h1>
          </div>

          {/* UTR (tax) status — compact line; the full add/update field lives
              on the payout-setup page. Hidden until loaded so it doesn't flash. */}
          {hasTaxUtr !== null ? (
            <div className="mb-4 flex flex-wrap items-center gap-2 font-['DM_Sans',sans-serif] text-[14px]">
              <span className="text-[#3a3335] font-bold">
                UTR (tax reference):
              </span>
              {hasTaxUtr ? (
                <span className="inline-flex items-center gap-1 text-[#1f7a3a] font-bold">
                  ✓ On file
                </span>
              ) : (
                <>
                  <span className="text-[#9a6700]">
                    Not provided — payouts are held until you add it.
                  </span>
                  <Link
                    to="/professional/setup-payouts"
                    className="text-[#8e2a2f] underline underline-offset-2 font-bold"
                  >
                    Add UTR
                  </Link>
                </>
              )}
            </div>
          ) : null}

          {/* Connect status card */}
          <div className="mb-8 rounded-xl border border-[#ede8e9] bg-white p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] mb-1">
                  {canReceivePayouts
                    ? 'Stripe payouts active'
                    : 'Set up Stripe payouts'}
                </h2>
                <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] max-w-[600px]">
                  {loading
                    ? 'Checking status…'
                    : canReceivePayouts
                      ? 'Your account is set up and payouts will land in your bank on Stripe’s schedule once a booking is marked complete.'
                      : status?.stripe_account_status === 'RESTRICTED'
                        ? 'Stripe needs more info before payouts can resume. Click below to finish the steps they’ve flagged.'
                        : status?.stripe_account_status === 'REJECTED'
                          ? 'Stripe rejected your application. Contact support if you think this was a mistake.'
                          : 'Bookings will be held as pending payouts until you complete Stripe onboarding (takes ~5 min — bank details + identity).'}
                </p>
                {status ? (
                  <div className="mt-2 flex flex-wrap gap-2 text-[12px]">
                    <Chip
                      label={`Payouts: ${status.payouts_enabled ? 'enabled' : 'disabled'}`}
                      ok={status.payouts_enabled}
                    />
                    <Chip
                      label={`Charges: ${status.charges_enabled ? 'enabled' : 'disabled'}`}
                      ok={status.charges_enabled}
                    />
                    <Chip
                      label={`Onboarding: ${status.onboarding_complete ? 'complete' : 'incomplete'}`}
                      ok={status.onboarding_complete}
                    />
                  </div>
                ) : null}

                {/* Surface Stripe's currently_due / past_due / disabled
                    reason verbatim so the pro knows exactly what
                    they need to fix. Stripe's `account_update` link
                    drops them on the failing field directly. */}
                {status && !status.payouts_ready ? (
                  <RequirementsList status={status} />
                ) : null}
              </div>
              {needsAction ? (
                <button
                  type="button"
                  onClick={handleStartOnboarding}
                  disabled={redirecting}
                  className="px-6 py-3 bg-[#8e2a2f] hover:bg-[#7a2428] text-white rounded-[8px] font-['DM_Sans',sans-serif] font-bold text-[14px] transition-colors disabled:opacity-50"
                >
                  {redirecting
                    ? 'Redirecting…'
                    : status?.stripe_account_status === 'NOT_STARTED'
                      ? 'Start onboarding'
                      : 'Continue onboarding'}
                </button>
              ) : canReceivePayouts ? (
                <button
                  type="button"
                  onClick={handleOpenStripeDashboard}
                  disabled={redirecting}
                  className="px-6 py-3 border border-[#8e2a2f] text-[#8e2a2f] hover:bg-[#8e2a2f] hover:text-white rounded-[8px] font-['DM_Sans',sans-serif] font-bold text-[14px] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {redirecting ? (
                    'Opening…'
                  ) : (
                    <>
                      <span className="material-icons text-[18px]">
                        open_in_new
                      </span>
                      View on Stripe
                    </>
                  )}
                </button>
              ) : null}
            </div>
          </div>

          {/* Earnings summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <SummaryCard
              label="Transferred"
              value={formatMoney(totals.PAID ?? 0, 'GBP')}
              accent
            />
            <SummaryCard
              label="Pending"
              value={formatMoney(totals.PENDING ?? 0, 'GBP')}
            />
            <SummaryCard
              label="Awaiting setup"
              value={formatMoney(totals.SKIPPED ?? 0, 'GBP')}
            />
          </div>

          {/* Payout history */}
          <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] mb-4">
            History
          </h2>
          {loading && payouts.length === 0 ? (
            <p className="text-sm text-[#908e8e]">Loading…</p>
          ) : payouts.length === 0 ? (
            <p className="text-sm text-[#908e8e]">
              No payouts yet — they appear here when bookings are completed.
            </p>
          ) : (
            <div className="rounded-xl border border-[#ede8e9] bg-white overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-[#fcfaf7] text-[#908e8e] text-[12px] uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-3 font-medium">Booking</th>
                    <th className="px-4 py-3 font-medium">Date</th>
                    <th className="px-4 py-3 font-medium text-right">
                      Gross
                    </th>
                    <th className="px-4 py-3 font-medium text-right">Fee</th>
                    <th className="px-4 py-3 font-medium text-right">Net</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {pagedPayouts.map((p) => (
                    <tr
                      key={p.id}
                      className="border-t border-[#ede8e9]"
                    >
                      <td className="px-4 py-3 font-mono text-[12px]">
                        {p.booking?.reference ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-[12px] text-[#908e8e]">
                        {formatDate(p.transferred_at ?? p.created_at)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {formatMoney(Number(p.gross_amount), p.currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-[#908e8e]">
                        − {formatMoney(Number(p.platform_fee_amount), p.currency)}
                      </td>
                      <td className="px-4 py-3 text-right font-bold">
                        {formatMoney(Number(p.net_amount), p.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-[11px] font-bold ${
                            STATUS_STYLE[p.display_status ?? p.status] ?? ''
                          }`}
                        >
                          {p.display_label ??
                            STATUS_LABEL[p.status] ??
                            p.status}
                        </span>
                        {p.error_message && p.status === 'FAILED' ? (
                          <p className="mt-1 text-[11px] text-red-600 max-w-[260px] line-clamp-2">
                            {p.error_message}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() =>
                            downloadProPayoutInvoice({
                              reference: p.booking?.reference ?? null,
                              scheduled_start:
                                p.booking?.scheduled_start ?? null,
                              payout_date: p.transferred_at ?? p.created_at,
                              currency: p.currency,
                              gross_amount: Number(p.gross_amount),
                              platform_fee_percentage: Number(
                                p.platform_fee_percentage,
                              ),
                              platform_fee_amount: Number(
                                p.platform_fee_amount,
                              ),
                              net_amount: Number(p.net_amount),
                              status: STATUS_LABEL[p.status] ?? p.status,
                              // Payouts only exist for completed
                              // bookings — every row here is finalised.
                              // The Stripe transfer state (PENDING /
                              // PAID / FAILED) is shown via `status`.
                              is_finalised: true,
                            })
                          }
                          aria-label="Download invoice"
                          title="Download invoice"
                          className="inline-flex items-center gap-1 px-2.5 h-[30px] rounded-[8px] border border-[#ede8e9] bg-white hover:border-[#8e2a2f] hover:text-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[12px] text-[#3a3335] transition-colors"
                        >
                          <span className="material-icons text-[16px]">
                            file_download
                          </span>
                          Invoice
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {payoutsTotalPages > 1 ? (
                <div className="flex justify-center py-4 border-t border-[#ede8e9]">
                  <Pagination
                    currentPage={payoutsPage}
                    totalPages={payoutsTotalPages}
                    onPageChange={setPayoutsPage}
                  />
                </div>
              ) : null}
            </div>
          )}

          {/* Cancellations the pro initiated. Pro-side cancels always
              trigger a 100% customer refund (per the cancel policy in
              Booking.service.ts), so this is money the pro didn't
              earn — useful to see in one place next to actual payouts. */}
          <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#3a3335] text-[18px] mt-12 mb-4">
            Your cancellations
          </h2>
          {loading && cancellations.length === 0 ? (
            <p className="text-sm text-[#908e8e]">Loading…</p>
          ) : cancellations.length === 0 ? (
            <p className="text-sm text-[#908e8e]">
              No cancellations on record — keep it up.
            </p>
          ) : (
            <div className="rounded-xl border border-[#ede8e9] bg-white overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-[#fcfaf7] text-[#908e8e] text-[12px] uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-3 font-medium">Booking</th>
                    <th className="px-4 py-3 font-medium">Customer</th>
                    <th className="px-4 py-3 font-medium">Cancelled</th>
                    <th className="px-4 py-3 font-medium">Reason</th>
                    <th className="px-4 py-3 font-medium text-right">
                      Refunded
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedCancellations.map((b) => {
                    const customerName =
                      [b.customer?.first_name, b.customer?.last_name]
                        .filter(Boolean)
                        .join(' ') ||
                      b.customer?.email ||
                      '—';
                    const refundLabel =
                      b.refund_outcome === 'CARD_REFUND'
                        ? 'card refund'
                        : b.refund_outcome === 'PARTIAL_CARD_REFUND'
                          ? 'part-refund (50%)'
                          : b.refund_outcome === 'WALLET_CREDIT'
                            ? 'wallet credit'
                            : 'no refund';
                    return (
                      <tr key={b.id} className="border-t border-[#ede8e9]">
                        <td className="px-4 py-3 font-mono text-[12px]">
                          {b.reference}
                        </td>
                        <td className="px-4 py-3 text-[12px] text-[#3a3335]">
                          {customerName}
                        </td>
                        <td className="px-4 py-3 text-[12px] text-[#908e8e]">
                          {formatDate(b.cancelled_at ?? null)}
                        </td>
                        <td className="px-4 py-3 text-[12px] text-[#908e8e] max-w-[260px]">
                          <span className="line-clamp-2">
                            {b.cancellation_reason || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-bold text-[#3a3335]">
                            {formatMoney(
                              Number(b.refund_amount ?? 0),
                              b.currency,
                            )}
                          </span>
                          <p className="text-[11px] text-[#908e8e]">
                            {refundLabel}
                          </p>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {cancellationsTotalPages > 1 ? (
                <div className="flex justify-center py-4 border-t border-[#ede8e9]">
                  <Pagination
                    currentPage={cancellationsPage}
                    totalPages={cancellationsTotalPages}
                    onPageChange={setCancellationsPage}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Chip({ label, ok }: { label: string; ok: boolean }) {
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
        ok
          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100'
          : 'bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200'
      }`}
    >
      {label}
    </span>
  );
}

function SummaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="rounded-xl border border-[#ede8e9] bg-white p-5">
      <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] uppercase tracking-wide">
        {label}
      </p>
      <p
        className={`mt-2 font-['Playfair_Display',sans-serif] font-bold text-[24px] ${
          accent ? 'text-[#8e2a2f]' : 'text-[#3a3335]'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

/**
 * Surfaces Stripe's `requirements.currently_due` / `past_due` /
 * `disabled_reason` verbatim so the pro can see exactly what's
 * blocking payouts. "Resolve on Stripe" opens an `account_update`
 * Account Link that drops the pro on the failing field(s) directly
 * — no full KYC re-run.
 *
 * The dashboard banner is the entry nudge; this list is the actionable
 * detail on the Payouts page. Both gate on the same `payouts_ready`
 * boolean so they stay in lockstep.
 */
function RequirementsList({ status }: { status: ProConnectStatus }) {
  const [opening, setOpening] = useState(false);
  const currentlyDue = status.currently_due ?? [];
  const pastDue = status.past_due ?? [];
  const disabledReason = status.disabled_reason;

  const handleResolve = async () => {
    setOpening(true);
    try {
      const res = await createProConnectUpdateLink();
      if (res?.url) {
        window.location.assign(res.url);
        return;
      }
      toast.error('Could not open Stripe. Please try again.');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not open Stripe.'));
    } finally {
      setOpening(false);
    }
  };

  if (
    currentlyDue.length === 0 &&
    pastDue.length === 0 &&
    !disabledReason
  ) {
    return null;
  }

  return (
    <div className="mt-4 rounded-lg border border-[#f3d6a0] bg-[#fff7e6] p-4">
      <p className="font-['DM_Sans',sans-serif] font-bold text-[#9a6700] text-[13px] mb-2">
        Stripe needs the following before payouts can start
      </p>
      {disabledReason ? (
        <p className="font-['DM_Sans',sans-serif] text-[#9a6700] text-[12px] mb-2">
          Reason: {humaniseRequirement(disabledReason)}
        </p>
      ) : null}
      {currentlyDue.length > 0 ? (
        <ul className="list-disc pl-5 text-[#3a3335] text-[12px] space-y-0.5 mb-2">
          {currentlyDue.map((item) => (
            <li key={`due-${item}`}>{humaniseRequirement(item)}</li>
          ))}
        </ul>
      ) : null}
      {pastDue.length > 0 ? (
        <>
          <p className="font-['DM_Sans',sans-serif] font-bold text-[#a8071a] text-[12px] mt-2 mb-1">
            Past due — blocks payouts now
          </p>
          <ul className="list-disc pl-5 text-[#a8071a] text-[12px] space-y-0.5 mb-2">
            {pastDue.map((item) => (
              <li key={`past-${item}`}>{humaniseRequirement(item)}</li>
            ))}
          </ul>
        </>
      ) : null}
      <button
        type="button"
        onClick={handleResolve}
        disabled={opening}
        className="mt-2 inline-flex items-center gap-2 px-4 h-[36px] rounded-[8px] bg-[#8e2a2f] text-white font-['DM_Sans',sans-serif] font-bold text-[13px] hover:bg-[#7a2428] disabled:opacity-50"
      >
        <span className="material-icons text-[16px]">open_in_new</span>
        {opening ? 'Opening Stripe…' : 'Resolve on Stripe'}
      </button>
    </div>
  );
}

// Stripe surfaces requirements via dotted machine codes
// ("external_account", "individual.dob.day"). Make them readable.
function humaniseRequirement(code: string): string {
  return code
    .replace(/^individual\./, '')
    .replace(/_/g, ' ')
    .replace(/\./g, ' › ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
